// Sourced from publicly available Nearpod lesson catalog (nearpod.com/t/market/init/).
// Each Pressto writing activity pairs with a real Nearpod lesson.

export interface NearpodLesson {
  program: string;
  nearpodTitle: string;
  grade: string;
  area: string;
  title: string;
  prompt: string;
  plan: string;
  purpose: string;
  importantWords: string[];
  standards: string[];
  templateUrl: string;
}

export interface NearpodPublisher {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  gradeRange: string;
  lessonCount: number;
  areaCount: number;
  areas: string[];
  lessons: NearpodLesson[];
}

export const NEARPOD_PUBLISHER: NearpodPublisher = {
  "slug": "nearpod",
  "name": "Nearpod",
  "tagline": "Pressto + Nearpod",
  "description": "Nearpod delivers the lesson. Pressto is where students write about it. Together: 68+ standards-aligned writing activities across ELA, Science, and Social Studies for grades K–8.",
  "gradeRange": "K–8",
  "lessonCount": 68,
  "areaCount": 3,
  "areas": ["ELA", "Science", "Social Studies"],
  "lessons": [
    {
      "program": "Nearpod",
      "nearpodTitle": "Personification: 2-3",
      "grade": "Grades 2–3",
      "area": "ELA",
      "title": "When Objects Come Alive",
      "prompt": "In today's Nearpod lesson, you explored how writers give human qualities to objects and animals through personification. Now it's your turn! Choose one object from your classroom or home and describe it as if it were a living creature with feelings, thoughts, and actions. Use at least three examples of personification to make your writing vivid and surprising.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["personification","human qualities","object","describe","vivid","feelings"],
      "standards": ["CCSS.ELA-LITERACY.W.2.2","CCSS.ELA-LITERACY.L.2.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Folktales and Tall Tales: 2-3",
      "grade": "Grades 2–3",
      "area": "ELA",
      "title": "Retell a Tall Tale in Your Own Words",
      "prompt": "In today's Nearpod lesson, you explored folktales and tall tales — stories with larger-than-life heroes and exaggerated adventures. Choose one tall tale you read or discussed today and retell it from beginning to end. Be sure to include the hero, the problem they faced, and how they solved it with their extraordinary abilities.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["folktale","tall tale","exaggeration","hero","legend","moral"],
      "standards": ["CCSS.ELA-LITERACY.W.3.3","CCSS.ELA-LITERACY.RL.3.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Eight Parts of Speech: 5-6",
      "grade": "Grades 5–6",
      "area": "ELA",
      "title": "All Eight at Work: A Descriptive Paragraph",
      "prompt": "In today's Nearpod lesson, you studied all eight parts of speech and how each one plays a unique role in a sentence. Write a descriptive paragraph about a place that matters to you — a park, a kitchen, a sports field — deliberately using all eight parts of speech. After your paragraph, label at least one example of each part of speech you used.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["noun","verb","adjective","adverb","pronoun","conjunction","preposition"],
      "standards": ["CCSS.ELA-LITERACY.W.5.2","CCSS.ELA-LITERACY.L.5.1"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "RACE Writing Strategy: 4-5",
      "grade": "Grades 4–5",
      "area": "ELA",
      "title": "Make Your Case with RACE",
      "prompt": "In today's Nearpod lesson, you learned the RACE strategy — Restate, Answer, Cite, Explain — for writing strong opinion responses. Choose a position on a topic your teacher provides, and write a paragraph using every step of RACE. Make sure your cited evidence comes directly from a text you have read, and that your explanation clearly connects the evidence to your answer.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["restate","answer","cite","explain","evidence","opinion","response"],
      "standards": ["CCSS.ELA-LITERACY.W.4.1","CCSS.ELA-LITERACY.W.4.1b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Personal Narrative Writing: Collecting Ideas: 1-2",
      "grade": "Grades 1–2",
      "area": "ELA",
      "title": "A Small Moment That Matters to Me",
      "prompt": "In today's Nearpod lesson, you practiced collecting ideas for personal narratives by thinking about special moments from your own life. Choose one small moment that you remembered today and write your own personal narrative about it. Tell what happened first, next, and last, and share how you felt during that moment.",
      "plan": "personal-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["personal narrative","memory","moment","feelings","details","beginning"],
      "standards": ["CCSS.ELA-LITERACY.W.1.3","CCSS.ELA-LITERACY.W.2.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Describing Characters Using Key Details: 1-2",
      "grade": "Grades 1–2",
      "area": "ELA",
      "title": "Two Characters, Two Descriptions",
      "prompt": "In today's Nearpod lesson, you practiced finding key details in a story to describe characters. Think about two characters from a book or story you know, and compare them by describing what each one looks like, says, and does. How are they similar? How are they different?",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["character","key details","describe","similar","different","traits"],
      "standards": ["CCSS.ELA-LITERACY.W.2.2","CCSS.ELA-LITERACY.RL.2.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Reading Comprehension: Exploring the Plot: 4-5",
      "grade": "Grades 4–5",
      "area": "ELA",
      "title": "Retell the Plot, Step by Step",
      "prompt": "In today's Nearpod lesson, you explored how a story's plot is built on exposition, rising action, climax, falling action, and resolution. Choose a story you read recently and retell its plot in order, making sure to include each stage. Explain how the conflict builds toward the climax and how it is finally resolved.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["plot","exposition","rising action","climax","falling action","resolution","conflict"],
      "standards": ["CCSS.ELA-LITERACY.W.4.3","CCSS.ELA-LITERACY.RL.4.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Personal Narrative Writing with a Mentor Text: 4-5",
      "grade": "Grades 4–5",
      "area": "ELA",
      "title": "My Own Story, Inspired by a Master",
      "prompt": "In today's Nearpod lesson, you studied a mentor text to discover the craft moves that make personal narratives powerful. Now write your own personal narrative about a meaningful moment in your life. Borrow at least two techniques from the mentor text — such as zooming in on details, using dialogue, or starting in the middle of the action.",
      "plan": "personal-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["mentor text","craft","dialogue","details","structure","voice","narrative"],
      "standards": ["CCSS.ELA-LITERACY.W.4.3","CCSS.ELA-LITERACY.W.4.3b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Grammar Rules: Apostrophes: 6-8",
      "grade": "Grades 6–8",
      "area": "ELA",
      "title": "What Happens When the Apostrophe Is Missing?",
      "prompt": "In today's Nearpod lesson, you explored the rules for using apostrophes in contractions and possessives. Explain the cause-and-effect relationship between incorrect apostrophe use and reader confusion. Use specific examples of sentences where a misplaced or missing apostrophe changes the meaning entirely, and explain what a reader might misunderstand as a result.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["apostrophe","contraction","possessive","grammar","punctuation","meaning","clarity"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.L.6.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Art of Persuasion: Common Fallacies: 7-12",
      "grade": "Grades 6–8",
      "area": "ELA",
      "title": "Spot the Fallacy, Make the Real Argument",
      "prompt": "In today's Nearpod lesson, you examined common logical fallacies that weaken persuasive writing and speeches. Choose a real-world issue you care about and write a persuasive paragraph that makes a strong, fallacy-free argument. Then identify one fallacy from today's lesson that a weak version of your argument might use, and explain why that fallacy fails.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["fallacy","logical","ad hominem","slippery slope","straw man","evidence","persuasion"],
      "standards": ["CCSS.ELA-LITERACY.W.6.1","CCSS.ELA-LITERACY.W.6.1b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Rhetoric in JFK's Inaugural Address: 7-12",
      "grade": "Grades 6–8",
      "area": "ELA",
      "title": "How JFK's Words Created a Moment",
      "prompt": "In today's Nearpod lesson, you analyzed the rhetorical devices JFK used in his Inaugural Address. Write a descriptive analysis of the speech, focusing on two or three specific rhetorical devices such as anaphora, antithesis, or alliteration. For each device, quote the passage and describe in precise detail how the language sounds, what it creates for the listener, and why it was effective.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["rhetoric","anaphora","antithesis","alliteration","ethos","pathos","logos"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.RI.6.6"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Public Speaking Tips: 6-12",
      "grade": "Grades 6–8",
      "area": "ELA",
      "title": "Solving Stage Fright",
      "prompt": "In today's Nearpod lesson, you explored tips and strategies for becoming a confident public speaker. Think about a common problem students face when speaking in front of others — such as stage fright, losing your place, or speaking too fast. Write a piece that clearly identifies the problem, explains what causes it, and proposes at least two practical solutions using strategies from today's lesson.",
      "plan": "problem-solution",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["public speaking","stage fright","eye contact","pacing","audience","confidence","delivery"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.SL.6.4"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Claims & Evidence in a Text: 3-5",
      "grade": "Grades 3–5",
      "area": "ELA",
      "title": "My Claim, My Proof",
      "prompt": "In today's Nearpod lesson, you practiced identifying claims and supporting evidence in nonfiction texts. Now write your own opinion piece about a topic from your reading or science or social studies class. State your claim clearly in the opening, then support it with at least two pieces of evidence from a text, and explain why each piece of evidence proves your point.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["claim","evidence","support","opinion","fact","text","reason"],
      "standards": ["CCSS.ELA-LITERACY.W.4.1","CCSS.ELA-LITERACY.W.4.1b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "News & News Values: 6-8",
      "grade": "Grades 6–8",
      "area": "ELA",
      "title": "What Makes a Story Newsworthy?",
      "prompt": "In today's Nearpod lesson, you explored the news values journalists use to decide which stories get covered — such as timeliness, proximity, and impact. Write a cause-and-effect piece explaining how the news values a journalist prioritizes directly affect which communities and events get covered and which get ignored. Use real or realistic examples to illustrate your thinking.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["news values","timeliness","proximity","impact","prominence","conflict","journalism"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.W.6.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Linking Words in Writing: 3-4",
      "grade": "Grades 3–4",
      "area": "ELA",
      "title": "How to Do Something, Step by Step",
      "prompt": "In today's Nearpod lesson, you practiced using linking words to connect ideas and show order in writing. Think of something you know how to do well — make a snack, play a game, or complete a school routine. Write a step-by-step explanation using linking words such as first, next, then, after that, and finally to guide your reader clearly through each stage.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["linking words","transition","first","next","finally","sequence","order"],
      "standards": ["CCSS.ELA-LITERACY.W.3.2","CCSS.ELA-LITERACY.W.3.2c"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Adjectives & Real-Life Connections: 2",
      "grade": "Grade 2",
      "area": "ELA",
      "title": "Describe It So I Can See It",
      "prompt": "In today's Nearpod lesson, you explored adjectives and practiced connecting them to real things in your world. Choose a favorite place — your bedroom, the playground, a restaurant — and describe it using as many strong adjectives as you can. Help your reader picture every sight, sound, and feeling of that place.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["adjective","describe","detail","color","size","texture","feeling"],
      "standards": ["CCSS.ELA-LITERACY.W.2.2","CCSS.ELA-LITERACY.L.2.1"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Drawing Inferences: 1-3",
      "grade": "Grades 1–3",
      "area": "ELA",
      "title": "Clues in the Story: Compare Two Inferences",
      "prompt": "In today's Nearpod lesson, you practiced drawing inferences by combining clues from a text with what you already know. Think about two moments in a story where you had to make an inference. Compare the two moments: what clues did the text give you each time, and what did you already know that helped you figure out what was really happening?",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["inference","clue","evidence","conclude","text","compare"],
      "standards": ["CCSS.ELA-LITERACY.W.3.2","CCSS.ELA-LITERACY.RL.3.1"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Interpreting Visual Information: 3-4",
      "grade": "Grades 3–4",
      "area": "ELA",
      "title": "Read the Image, Write the Story It Tells",
      "prompt": "In today's Nearpod lesson, you practiced interpreting charts, maps, diagrams, and photos to understand information. Choose one visual from today's lesson and write a detailed description of what it shows. Explain what information it communicates, what details stand out, and what a reader would understand from it that words alone might not show.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["visual","diagram","caption","data","interpret","detail","information"],
      "standards": ["CCSS.ELA-LITERACY.W.3.2","CCSS.ELA-LITERACY.RI.3.7"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Supporting Ideas: 3-4",
      "grade": "Grades 3–4",
      "area": "ELA",
      "title": "One Opinion, Three Reasons",
      "prompt": "In today's Nearpod lesson, you practiced identifying and writing supporting ideas that hold up a main point. Choose something you have a strong opinion about — a rule at school, a book you love, a change you'd make to your community. State your opinion clearly, then write three supporting reasons, each one developed with a specific detail or example that truly backs up your claim.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["opinion","support","reason","evidence","detail","main idea","conclusion"],
      "standards": ["CCSS.ELA-LITERACY.W.3.1","CCSS.ELA-LITERACY.W.3.1b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Connotation & Denotation: 6-8",
      "grade": "Grades 6–8",
      "area": "ELA",
      "title": "Same Word, Different Worlds",
      "prompt": "In today's Nearpod lesson, you explored how words can share a dictionary definition but carry very different emotional weight through their connotation. Choose two words with a similar denotation but contrasting connotations — such as 'slender' and 'scrawny,' or 'confident' and 'arrogant.' Write a comparison that explains what each word technically means, what feelings or images each one creates, and why a skilled writer would choose one over the other.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["connotation","denotation","tone","word choice","meaning","nuance","implication"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.L.6.5"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Fables: 1-2",
      "grade": "Grades 1–2",
      "area": "ELA",
      "title": "Retell Your Favorite Fable",
      "prompt": "In today's Nearpod lesson, you read and explored fables — short stories with animal characters that teach a lesson called a moral. Choose one fable from today's lesson and retell it in your own words. Include the animal characters, the problem they faced, what happened, and the moral at the end.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["fable","moral","character","animal","lesson","beginning"],
      "standards": ["CCSS.ELA-LITERACY.W.2.3","CCSS.ELA-LITERACY.RL.2.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Animal Classification: 3",
      "grade": "Grade 3",
      "area": "Science",
      "title": "Sort It Out: Comparing Two Animal Groups",
      "prompt": "In today's Nearpod lesson, you explored how scientists classify animals into groups based on their traits. Choose two animal groups you learned about — such as mammals and reptiles — and write to explain how they are alike and how they are different. Use at least two traits to compare them, and be sure to use science vocabulary from the lesson.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["classify","vertebrate","invertebrate","mammal","reptile","trait","organism"],
      "standards": ["CCSS.ELA-LITERACY.W.3.2","CCSS.ELA-LITERACY.W.3.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Weather Instruments: 5",
      "grade": "Grade 5",
      "area": "Science",
      "title": "The Right Tool for the Job: Describing a Weather Instrument",
      "prompt": "In today's Nearpod lesson, you investigated the tools meteorologists use to measure weather. Choose one weather instrument that you found most interesting and write a detailed description of it. Explain what it measures, how it works, and why it is important for weather forecasting.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["meteorologist","barometer","anemometer","thermometer","precipitation","forecast"],
      "standards": ["CCSS.ELA-LITERACY.W.5.2","CCSS.ELA-LITERACY.W.5.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Alternative Energy: Renewable Resources: 3-5",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "Solving Our Energy Problem: A Renewable Solution",
      "prompt": "In today's Nearpod lesson, you explored renewable energy sources like solar, wind, and water power. Many communities still depend on fossil fuels that pollute the air and will eventually run out. Write to explain the problem of depending on nonrenewable energy sources, and describe at least one renewable solution that could help solve it.",
      "plan": "problem-solution",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["renewable","nonrenewable","fossil fuel","solar energy","wind energy","sustainable","resource"],
      "standards": ["CCSS.ELA-LITERACY.W.4.2","CCSS.ELA-LITERACY.W.4.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Earth's Atmosphere: 5-6",
      "grade": "Grades 5–6",
      "area": "Science",
      "title": "Layer by Layer: How the Atmosphere Shapes Life on Earth",
      "prompt": "In today's Nearpod lesson, you learned about the layers of Earth's atmosphere and the gases they contain. Explain how the composition of the atmosphere causes conditions on Earth that make life possible. What would happen if one of those atmospheric layers changed or disappeared?",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["atmosphere","troposphere","ozone layer","nitrogen","oxygen","greenhouse gas","ultraviolet"],
      "standards": ["CCSS.ELA-LITERACY.W.5.2","CCSS.ELA-LITERACY.W.5.2b","CCSS.ELA-LITERACY.W.6.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Body Systems: Introduction: 3-5",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "Which Body System Matters Most?",
      "prompt": "In today's Nearpod lesson, you were introduced to the major systems of the human body and the jobs they do. In your opinion, which body system is the most important for keeping you alive and healthy? Write to state your opinion and support it with at least two reasons from what you learned in the lesson.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["body system","organ","function","skeletal","muscular","digestive","nervous"],
      "standards": ["CCSS.ELA-LITERACY.W.4.1","CCSS.ELA-LITERACY.W.4.1b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Observing Moon Phases: 4-5",
      "grade": "Grades 4–5",
      "area": "Science",
      "title": "My Nights with the Moon: A Personal Observation Story",
      "prompt": "Imagine that after today's Nearpod lesson about moon phases, you go outside every night for a month and watch the moon change. Write a personal narrative about what you notice on several different nights. Describe what you see, what you wonder, and what you finally understand about why the moon seems to change shape.",
      "plan": "personal-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["moon phase","waxing","waning","crescent","full moon","new moon","lunar cycle"],
      "standards": ["CCSS.ELA-LITERACY.W.4.3","CCSS.ELA-LITERACY.W.4.3b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Rock Cycle: 4-6",
      "grade": "Grades 4–6",
      "area": "Science",
      "title": "A Rock's Long Journey: Retelling the Rock Cycle",
      "prompt": "In today's Nearpod lesson, you followed the journey of rock as it transforms over millions of years. Retell the story of a single rock as it travels through the rock cycle — from being buried deep underground, to melting, cooling, and being worn away at the surface. Write in a way that shows each stage of the journey and what causes the rock to change.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["igneous","sedimentary","metamorphic","magma","erosion","pressure","crystallization"],
      "standards": ["CCSS.ELA-LITERACY.W.5.3","CCSS.ELA-LITERACY.W.5.3b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Symbiosis Among Animals: 6-7",
      "grade": "Grades 6–7",
      "area": "Science",
      "title": "Not All Partnerships Are Equal: Comparing Symbiotic Relationships",
      "prompt": "In today's Nearpod lesson, you explored three types of symbiotic relationships: mutualism, commensalism, and parasitism. Choose two of these relationships and write to compare and contrast them. Use specific examples from the lesson to show how each relationship affects the organisms involved, and explain what makes them different from each other.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["symbiosis","mutualism","commensalism","parasitism","host","parasite","organism"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.W.6.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Potential and Kinetic Energy: 6-8",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "Stored vs. Moving: Comparing Potential and Kinetic Energy",
      "prompt": "In today's Nearpod lesson, you investigated the difference between potential and kinetic energy. Write to compare and contrast these two forms of energy. Explain what each type is, how they are related, and use at least one real-world example from the lesson to show how energy can transform from one type to the other.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["potential energy","kinetic energy","stored energy","motion","mass","velocity","transformation"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2","CCSS.ELA-LITERACY.W.7.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Changes in Matter: 5-6",
      "grade": "Grades 5–6",
      "area": "Science",
      "title": "What Made That Happen? Explaining Changes in Matter",
      "prompt": "In today's Nearpod lesson, you explored how matter can change physically and chemically. Choose one physical change and one chemical change from the lesson and explain what causes each change and what effects it produces. How can you tell that a new substance has — or has not — been formed?",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["physical change","chemical change","matter","substance","reaction","evidence","property"],
      "standards": ["CCSS.ELA-LITERACY.W.5.2","CCSS.ELA-LITERACY.W.5.2b","CCSS.ELA-LITERACY.W.6.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Food Chains & Food Webs: 5",
      "grade": "Grade 5",
      "area": "Science",
      "title": "Save the Predators? An Opinion on Food Web Balance",
      "prompt": "In today's Nearpod lesson, you learned how energy moves through food chains and food webs. Some people think that predators at the top of the food chain should be protected, even when they are dangerous to humans and livestock. Do you agree or disagree? Write to state your opinion and support it with at least two reasons based on what you learned about food webs.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["food chain","food web","predator","prey","producer","consumer","ecosystem"],
      "standards": ["CCSS.ELA-LITERACY.W.5.1","CCSS.ELA-LITERACY.W.5.1b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Digestive System: 6-8",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "Step by Step: How Your Body Digests Food",
      "prompt": "In today's Nearpod lesson, you traced the path food takes through the digestive system. Write to explain the step-by-step process of digestion, from the moment food enters your mouth to the moment nutrients are absorbed by your body. Be sure to name the organs involved at each stage and explain what job each one does.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["digestion","esophagus","stomach","small intestine","enzyme","nutrient","absorption"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2","CCSS.ELA-LITERACY.W.7.2a","CCSS.ELA-LITERACY.W.7.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Plant Parts & Their Jobs: 1",
      "grade": "Grade 1",
      "area": "Science",
      "title": "All About Plant Parts",
      "prompt": "In today's Nearpod lesson, you learned about the parts of a plant and what each part does. Choose your favorite part of a plant and describe it. What does it look like, and what is its special job for helping the plant live and grow?",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["roots","stem","leaves","flower","seed","nutrients","sunlight"],
      "standards": ["CCSS.ELA-LITERACY.W.1.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Magnets & Magnetic Fields: 5",
      "grade": "Grade 5",
      "area": "Science",
      "title": "Forces You Can't See: Explaining Magnetic Effects",
      "prompt": "In today's Nearpod lesson, you explored how magnets create invisible magnetic fields that can attract or repel objects. Choose one example from the lesson and explain the cause-and-effect relationship at work. What causes the magnetic force, and what effects does it produce on the materials nearby?",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["magnet","magnetic field","attract","repel","poles","force","iron"],
      "standards": ["CCSS.ELA-LITERACY.W.5.2","CCSS.ELA-LITERACY.W.5.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Water Cycle: 5-6",
      "grade": "Grades 5–6",
      "area": "Science",
      "title": "Follow the Drop: Explaining the Water Cycle Step by Step",
      "prompt": "In today's Nearpod lesson, you traced how water moves continuously through the environment. Write to explain the stages of the water cycle in order, from evaporation through precipitation and back again. Be sure to name each stage, explain what causes the water to change state, and describe what happens to the water at each step.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["evaporation","condensation","precipitation","transpiration","runoff","water vapor","groundwater"],
      "standards": ["CCSS.ELA-LITERACY.W.5.2","CCSS.ELA-LITERACY.W.5.2a","CCSS.ELA-LITERACY.W.5.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Physical & Chemical Weathering: 4-6",
      "grade": "Grades 4–6",
      "area": "Science",
      "title": "Two Ways Rocks Break Down: Physical vs. Chemical Weathering",
      "prompt": "In today's Nearpod lesson, you discovered that rocks can be broken down in very different ways. Write to compare and contrast physical weathering and chemical weathering. Explain how each process works, give an example of each, and describe how they are alike and how they are different.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["weathering","physical weathering","chemical weathering","erosion","acid","frost","mineral"],
      "standards": ["CCSS.ELA-LITERACY.W.5.2","CCSS.ELA-LITERACY.W.5.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Mass, Distance, & Gravity: 6",
      "grade": "Grade 6",
      "area": "Science",
      "title": "Why Things Fall: Cause and Effect in Gravity",
      "prompt": "In today's Nearpod lesson, you explored how mass and distance affect the force of gravity between objects. Choose two factors you learned about — such as increasing mass or increasing distance — and explain what causes the change in gravitational force and what effect that produces. Use specific examples from the lesson to support your explanation.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["gravity","mass","distance","force","orbit","attraction","weight"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.W.6.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Density: 6-8",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "Why Does Steel Float? Solving a Density Problem",
      "prompt": "In today's Nearpod lesson, you learned that density determines whether objects sink or float. Here is a real engineering problem: steel is much denser than water, yet massive steel ships float. Explain why this is a problem if you think only about density, and then describe the solution engineers use to make heavy steel ships stay afloat.",
      "plan": "problem-solution",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["density","buoyancy","mass","volume","displacement","float","sink"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2","CCSS.ELA-LITERACY.W.7.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Heat Transfer: Convection: 6-8",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "Heat on the Move: Explaining How Convection Works",
      "prompt": "In today's Nearpod lesson, you investigated how heat transfers through fluids via convection currents. Explain what causes a convection current to form and what effects it produces. Use a specific example from the lesson — such as ocean currents or boiling water — and walk the reader through the chain of causes and effects.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["convection","heat transfer","fluid","current","density","temperature","circulation"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2","CCSS.ELA-LITERACY.W.7.2a","CCSS.ELA-LITERACY.W.7.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Respiratory System: 6-8",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "Every Breath You Take: How the Respiratory System Works",
      "prompt": "In today's Nearpod lesson, you traced the path of air through the respiratory system. Write to explain in order what happens from the moment you take a breath to the moment oxygen enters your bloodstream. Name each structure along the path and explain what it does at each step.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["trachea","bronchi","alveoli","diaphragm","oxygen","carbon dioxide","gas exchange"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2","CCSS.ELA-LITERACY.W.7.2a","CCSS.ELA-LITERACY.W.7.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "States of Matter: 3",
      "grade": "Grade 3",
      "area": "Science",
      "title": "Solid, Liquid, or Gas? Comparing States of Matter",
      "prompt": "In today's Nearpod lesson, you learned that matter can exist as a solid, a liquid, or a gas. Choose two states of matter and write to compare and contrast them. Explain how the particles are arranged in each state, what properties each one has, and give one real-world example of each.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["solid","liquid","gas","matter","particles","property","volume"],
      "standards": ["CCSS.ELA-LITERACY.W.3.2","CCSS.ELA-LITERACY.W.3.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Climate Zones: Temperate: 5-7",
      "grade": "Grades 5–7",
      "area": "Science",
      "title": "Hot, Cold, or Just Right? Comparing Climate Zones",
      "prompt": "In today's Nearpod lesson, you explored the characteristics of temperate climate zones. Compare the temperate climate zone with one other climate zone you know about — such as tropical or polar. Explain how their temperatures, precipitation, and plant life are alike and different, and what causes those differences.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["climate zone","temperate","precipitation","latitude","season","vegetation","biome"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2","CCSS.ELA-LITERACY.W.6.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Parts of Plants & Animals: K",
      "grade": "Kindergarten",
      "area": "Science",
      "title": "My Favorite Animal Part",
      "prompt": "In today's Nearpod lesson, you learned about the parts of plants and animals. Draw and write about your favorite animal. Tell what one body part looks like and what that part helps the animal do.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["body part","animal","plant","leaf","root","wing","tail"],
      "standards": ["CCSS.ELA-LITERACY.W.K.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Contact & Non-Contact Forces: 3-6",
      "grade": "Grades 3–6",
      "area": "Science",
      "title": "Touch or No Touch? Comparing Contact and Non-Contact Forces",
      "prompt": "In today's Nearpod lesson, you explored forces that require contact and forces that act at a distance. Write to compare and contrast contact forces and non-contact forces. Explain how each type of force works, give one example of each, and describe what makes them different from each other.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["force","contact force","non-contact force","gravity","friction","magnetic force","push"],
      "standards": ["CCSS.ELA-LITERACY.W.4.2","CCSS.ELA-LITERACY.W.4.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Circulatory System: 6-8",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "One Full Loop: Tracing Blood Through the Circulatory System",
      "prompt": "In today's Nearpod lesson, you followed the path of blood through the circulatory system. Write to explain in sequence how blood travels from the heart to the lungs, back to the heart, and then out to the rest of the body. Name the key structures at each step and explain what each one does.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["heart","artery","vein","capillary","oxygen","circulation","pulmonary"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2","CCSS.ELA-LITERACY.W.7.2a","CCSS.ELA-LITERACY.W.7.2b"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Wants and Needs: K-2",
      "grade": "Grades K–2",
      "area": "Social Studies",
      "title": "My Most Important Need",
      "prompt": "In today's Nearpod lesson, you learned the difference between wants and needs. Think about one need that matters most to your family — like food, shelter, or clothing. Write a personal story about a time when that need was met, and explain how it made you feel.",
      "plan": "personal-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["wants","needs","shelter","clothing","food","community"],
      "standards": ["CCSS.ELA-LITERACY.W.2.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "US Symbols & Good Citizenship: K",
      "grade": "Kindergarten",
      "area": "Social Studies",
      "title": "What Makes a Good Citizen?",
      "prompt": "In your Nearpod lesson, you explored US symbols and what it means to be a good citizen. Do you think being kind to others is the most important rule of good citizenship? Use what you learned to share your opinion and give one reason why.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["citizen","symbol","flag","respect","rule","community"],
      "standards": ["CCSS.ELA-LITERACY.W.K.1"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Trail of Tears: 4-5",
      "grade": "Grades 4–5",
      "area": "Social Studies",
      "title": "Forced From Their Homeland",
      "prompt": "In today's Nearpod lesson, you learned about the forced removal of the Cherokee Nation and other tribes along the Trail of Tears. Retell the key events of this journey from the perspective of a historian, including what caused the removal, what happened along the route, and what the consequences were for the Cherokee people.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["removal","Cherokee","Indian Removal Act","forced march","sovereignty","territory"],
      "standards": ["CCSS.ELA-LITERACY.W.4.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Columbian Exchange: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "A Trade That Changed the World",
      "prompt": "Today's Nearpod lesson explored the Columbian Exchange — the massive transfer of plants, animals, diseases, and ideas between the Americas and Europe after 1492. Explain the most significant causes of the Columbian Exchange and describe its effects on both the Old World and the New World. Use specific examples from the lesson to support your explanation.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["exchange","hemisphere","disease","agriculture","conquest","trade routes","indigenous"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Maps: K-2",
      "grade": "Grades K–2",
      "area": "Social Studies",
      "title": "All About My Neighborhood Map",
      "prompt": "In your Nearpod lesson, you discovered how maps help us understand the world around us. Describe what you would put on a map of your neighborhood or school. What important places would you include, and what map tools — like a compass or a key — would you use to help people read it?",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["map","compass rose","map key","symbol","location","neighborhood"],
      "standards": ["CCSS.ELA-LITERACY.W.2.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Congressional Committees: 6-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "Fix It: A Proposal for Better Lawmaking",
      "prompt": "In today's Nearpod lesson, you explored how Congressional committees review and shape legislation. Many people argue that the committee system can slow down or block important laws. Write a proposal explaining one specific problem with how Congressional committees work and suggest a solution that would make the process more fair and effective.",
      "plan": "problem-solution",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["committee","legislation","Congress","Senate","subcommittee","markup","filibuster"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Producers and Consumers: 2",
      "grade": "Grade 2",
      "area": "Social Studies",
      "title": "Producers and Consumers in My Community",
      "prompt": "In today's Nearpod lesson, you learned about producers — people who make goods or provide services — and consumers, who buy and use them. Describe one producer and one consumer from your own community. Explain what each person does and how they depend on each other.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["producer","consumer","goods","services","community","trade"],
      "standards": ["CCSS.ELA-LITERACY.W.2.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Colonization & Africa: 6-8",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "The Problem of Colonial Rule",
      "prompt": "In today's Nearpod lesson, you examined how European powers colonized Africa and the consequences for African peoples and societies. Identify the most significant problem that colonization created for African communities and propose a solution or response — either historical or modern — that addresses that harm. Use evidence from the lesson to support your writing.",
      "plan": "problem-solution",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["colonization","imperialism","sovereignty","exploitation","resistance","partition","independence"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Missouri Compromise: 4-5",
      "grade": "Grades 4–5",
      "area": "Social Studies",
      "title": "Was the Missouri Compromise a Fair Deal?",
      "prompt": "In today's Nearpod lesson, you explored the Missouri Compromise of 1820 and why Congress created it. Do you think the Missouri Compromise was a fair solution to the conflict over slavery in new states? State your opinion clearly and support it with at least two reasons from what you learned.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["compromise","slavery","free state","slave state","Congress","boundary","territory"],
      "standards": ["CCSS.ELA-LITERACY.W.4.1"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Supply, Demand & Price: 5-7",
      "grade": "Grades 5–7",
      "area": "Social Studies",
      "title": "Why Prices Change: A Supply and Demand Explanation",
      "prompt": "In today's Nearpod lesson, you explored how supply, demand, and price are connected. Choose a real product — like a video game, sneakers, or a popular toy — and explain what happens to its price when supply goes down or demand goes up. Use the economic concepts from the lesson to walk your reader through the cause-and-effect relationship.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["supply","demand","price","scarcity","market","equilibrium","consumer"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Why We Have Rules: K-1",
      "grade": "Grades K–1",
      "area": "Social Studies",
      "title": "A Rule That Matters to Me",
      "prompt": "In your Nearpod lesson, you learned about why communities have rules. Think of one rule you follow at home or at school. Write a short story about a time when that rule helped keep things safe or fair.",
      "plan": "personal-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["rules","fair","safe","community","responsibility"],
      "standards": ["CCSS.ELA-LITERACY.W.1.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Rise of Islamic Empires: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "How Islamic Empires Rose to Power",
      "prompt": "In today's Nearpod lesson, you explored the conditions that led to the rise of Islamic empires across the Middle East, North Africa, and beyond. Explain the key causes behind the rapid expansion of Islamic empires and describe the effects this growth had on trade, culture, and knowledge. Use specific details from the lesson.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["empire","caliphate","expansion","trade","Islam","scholarship","conquest"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Classical China: Zhou, Qin & Han Dynasties: 6-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "Three Dynasties, Three Visions of China",
      "prompt": "In today's Nearpod lesson, you examined the Zhou, Qin, and Han dynasties of Classical China. Compare and contrast two of these dynasties — focusing on how each ruler governed, what each dynasty valued, and how they shaped Chinese civilization. Use specific evidence from the lesson.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["dynasty","Confucianism","Legalism","Silk Road","emperor","centralization","philosophy"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Fall of Rome: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "Why Rome Fell: Causes and Consequences",
      "prompt": "In today's Nearpod lesson, you investigated the multiple factors behind the fall of the Roman Empire. Explain the most important causes of Rome's collapse and describe the effects that followed for Europe and the Mediterranean world. Be specific — use examples and evidence from the lesson to support your explanation.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["empire","decline","inflation","invasion","corruption","military","barbarian"],
      "standards": ["CCSS.ELA-LITERACY.W.8.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Progressivism: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "Should Progressives Have Gone Further?",
      "prompt": "In today's Nearpod lesson, you explored the Progressive Era — a time when reformers fought to fix problems caused by industrialization, corruption, and inequality. Do you think the Progressives achieved enough change, or should they have pushed for more radical reforms? State your opinion and back it up with specific evidence from the lesson.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["reform","muckraker","regulation","amendment","corruption","industrialization","suffrage"],
      "standards": ["CCSS.ELA-LITERACY.W.7.1"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Early African Societies: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "The Richness of Early African Civilizations",
      "prompt": "In today's Nearpod lesson, you explored early African societies and the ways they were organized, governed, and connected through trade and culture. Describe one early African society in depth — explaining its geography, economy, social structure, and cultural achievements. Use specific details from the lesson to bring this civilization to life for your reader.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["civilization","trade route","kinship","oral tradition","savanna","agriculture","kingdom"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Post-Classical China: Sui, Tang & Song: 6-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "Rebuilding and Reinventing China",
      "prompt": "In today's Nearpod lesson, you explored how the Sui, Tang, and Song dynasties rebuilt China and launched a period of innovation and expansion. Explain the sequence of key developments across these three dynasties — from the Sui's reunification to the Tang's golden age to the Song's technological breakthroughs. Walk your reader through the events in order, showing how each dynasty built on the last.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["reunification","dynasty","bureaucracy","gunpowder","printing","tributary system","Song"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Introduction to US Government: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "How the Three Branches Work Together",
      "prompt": "In today's Nearpod lesson, you were introduced to the structure of the United States government and its three branches. Describe how the three branches — legislative, executive, and judicial — are organized, what powers each holds, and how the system of checks and balances keeps any one branch from becoming too powerful.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["legislative","executive","judicial","checks and balances","separation of powers","Constitution","veto"],
      "standards": ["CCSS.ELA-LITERACY.W.7.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "Civilizations: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "What Makes a Society a Civilization?",
      "prompt": "In today's Nearpod lesson, you explored the defining characteristics of civilizations — from writing systems to social hierarchy to organized religion. Compare two early civilizations you learned about, examining both what they shared and what made each unique. Which characteristics do you think were most essential to their development?",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["civilization","city-state","agriculture","social hierarchy","government","writing","culture"],
      "standards": ["CCSS.ELA-LITERACY.W.6.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "World War II: Effects of the War: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "A World Transformed: The Aftermath of World War II",
      "prompt": "In today's Nearpod lesson, you examined the far-reaching effects of World War II on nations, societies, and the global order. Explain the most significant effects of the war — including political, economic, and human consequences — and analyze how they reshaped the modern world. Use specific evidence from the lesson.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": ["aftermath","Holocaust","United Nations","Cold War","reconstruction","nuclear","decolonization"],
      "standards": ["CCSS.ELA-LITERACY.W.8.2"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Korean War: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "The Forgotten War: Retelling Korea's Conflict",
      "prompt": "In today's Nearpod lesson, you explored the causes, key events, and outcome of the Korean War. Retell the story of the Korean War — from the invasion that started the conflict, through the major turning points, to the armistice that ended the fighting. Be sure to explain why this war matters in the larger story of the Cold War era.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": ["armistice","peninsula","containment","UN forces","38th parallel","Cold War","stalemate"],
      "standards": ["CCSS.ELA-LITERACY.W.8.3"],
      "templateUrl": ""
    },
    {
      "program": "Nearpod",
      "nearpodTitle": "The Presidency of John F. Kennedy: 7-12",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "Kennedy's Legacy: Promise, Crisis, and Change",
      "prompt": "In today's Nearpod lesson, you explored John F. Kennedy's presidency — from the Cuban Missile Crisis to the Civil Rights Movement to the space race. Do you think Kennedy's presidency had a lasting positive impact on the United States? State your opinion clearly and support it with at least two specific pieces of evidence from the lesson.",
      "plan": "opinion",
      "purpose": "Opinion Writing",
      "importantWords": ["presidency","Cuban Missile Crisis","civil rights","space race","New Frontier","assassination","Cold War"],
      "standards": ["CCSS.ELA-LITERACY.W.8.1"],
      "templateUrl": ""
    }
  ]
};

